import { action, computed, makeObservable, observable } from 'mobx';
import { duration } from '../../components/utils';
import { isDefinedObject, Maybe, Nullable, ObjectLiteral } from '../../core';
import { getTimeRegionsMergedDuration } from '../../core/time';
import { fromMoments, JobModel, MomentModel } from '..';
import { Store } from '../../store/store';
import { EntitySelector, SelectorCoreData } from '../selector';
import { BindingProps, StoreNode } from '../../store';
import { Speaker, SpeakerModel } from '../speaker';
import { Answer, Question, SubTopic, Topic } from '../moment';
import { areConnected, matches } from './momentFilters';
import intersectionBy from 'lodash/intersectionBy';
import { speakerMatches } from '../speakers';

export const MOMENT_SELECTOR_STATE_KEY = 'momentSelector.state';

const DEFAULT_MODE: MomentSelectorMode = 'Topics';
const ALLOWED_MODES: Set<MomentSelectorMode> = new Set([
  'Topics',
  'Speakers',
  'Interview'
]);

type Props = {
  jobId?: Nullable<string>;
}

export type MomentSelectorMode =
  'Topics' |
  'Speakers' |
  'Companies' |
  'Interview';

export type MomentSelectorQualifiedMode =
  'Video' |
  'Topics' |
  'TopicsVideo' |
  'Interview' | 
  'InterviewVideo' |
  'Speakers' |
  'SpeakersVideo' |
  'Companies' |
  'CompaniesVideo';

export type MomentSelectorOutput = {

  mode: MomentSelectorMode,
  isVideoModeEnabled: boolean,

  topicGroupKeys: Set<string>,
  genericGroupKeys: Set<string>,
  speakerGroupKeys: Set<string>
}

export type MomentSelectorData = {
  mode: MomentSelectorMode,
  isVideoModeEnabled: boolean,

  topicSelector: SelectorCoreData<string>,
  subTopicSelector: SelectorCoreData<string>,
  speakerSelector: SelectorCoreData<string>,
  answerSelector: SelectorCoreData<string>,
  questionSelector: SelectorCoreData<string>
}

export class MomentSelector
  extends StoreNode {

  constructor(store: Store, props?: BindingProps<Props>) {
    super(store, props);
    makeObservable(this, {
      mode: observable,
      isVideoModeEnabled: observable,
      highlightedMomentId: observable,
      highlightedMoment: computed,
      isTopicsMode: computed,
      isSpeakersMode: computed,
      isCompaniesMode: computed,
      isInterviewMode: computed,
      outputMode: computed,
      isTopicsOutputMode: computed,
      isSpeakersOutputMode: computed,
      isCompaniesOutputMode: computed,
      isVideoOutputMode: computed,
      qualifiedMode: computed,
      isImplicitVideoOutputMode: computed,
      searchQuery: observable,
      setSearchQuery: action,
      clearSearchQuery: action,
      jobId: computed,
      job: computed,
      isEmpty: computed,
      queryable: computed,
      moments: computed,
      speakers: computed,
      hasSpeakers: computed,
      generics: computed,
      transcripts: computed,
      setHighlightedMoment: action,
      clearHighlightedMoment: action,
      topicSearchQuery: observable,
      topics: computed,
      hasTopics: computed,
      selectedTopics: computed,
      filteredTopics: computed,
      filteredSelectedTopics: computed,
      topicFilteredKeys: computed,
      toggleTopic: action,
      selectTopic: action,
      unselectTopic: action,
      selectAllTopics: action,
      clearTopics: action,
      setTopicSearchQuery: action,
      clearTopicSearchQuery: action,
      subTopicSearchQuery: observable,
      subTopics: computed,
      hasSubTopics: computed,
      selectedSubTopics: computed,
      filteredSubTopics: computed,
      filteredSelectedSubTopics: computed,
      subTopicFilteredKeys: computed,
      toggleSubTopic: action,
      selectSubTopic: action,
      unselectSubTopic: action,
      selectAllSubTopics: action,
      clearSubTopics: action,
      setSubTopicSearchQuery: action,
      clearSubTopicSearchQuery: action,
      selectSubTopicDefaults: action,
      questionSearchQuery: observable,
      questions: computed,
      hasQuestions: computed,
      selectedQuestions: computed,
      filteredQuestions: computed,
      filteredSelectedQuestions: computed,
      questionFilteredKeys: computed,
      toggleQuestion: action,
      selectQuestion: action,
      unselectQuestion: action,
      selectAllQuestions: action,
      clearQuestions: action,
      setQuestionSearchQuery: action,
      clearQuestionSearchQuery: action,
      answerSearchQuery: observable,
      answers: computed,
      hasAnswers: computed,
      selectedAnswers: computed,
      filteredAnswers: computed,
      filteredSelectedAnswers: computed,
      answerFilteredKeys: computed,
      toggleAnswer: action,
      selectAnswer: action,
      unselectAnswer: action,
      selectAllAnswers: action,
      clearAnswers: action,
      setAnswerSearchQuery: action,
      clearAnswerSearchQuery: action,
      selectAnswerDefaults: action,
      selectedSpeakerIds: computed,
      selectedSpeakers: computed,
      filteredSpeakers: computed,
      filteredSelectedSpeakers: computed,
      speakerFilteredKeys: computed,
      toggleSpeaker: action,
      selectSpeaker: action,
      unselectSpeaker: action,
      selectAllSpeakers: action,
      clearSpeakers: action,
      selectAll: action,
      unselectAll: action,
      selectedTotalDuration: computed,
      selectedTotalSavedDuration: computed,
      clear: action,
      setMode: action,
      setModeToTopics: action,
      setModeToSpeakers: action,
      setModeToInterview: action,
      enterVideoMode: action,
      exitVideoMode: action,
      reset: action,
      import: action,
      serialize: action,
      deserialize: action,
      load: action,
      purge: action,
      handleMomentMutated: action,
    });

    this.topicSelector = new EntitySelector(this.store, {
      name: 'TopicSelector',
      entities: () => this.topics,
      filteredKeys: () => this.topicFilteredKeys
    });

    this.subTopicSelector = new EntitySelector(this.store, {
      name: 'SubTopicSelector',
      entities: () => this.subTopics,
      filteredKeys: () => this.subTopicFilteredKeys,
      useInvertedSelector: true
    });

    this.speakerSelector = new EntitySelector(this.store, {
      name: 'SpeakerSelector',
      entities: () => this.speakers,
      filteredKeys: () => this.speakerFilteredKeys
    });

    this.questionSelector = new EntitySelector(this.store, {
      name: 'QuestionSelector',
      entities: () => this.questions,
      filteredKeys: () => this.questionFilteredKeys
    });

    this.answerSelector = new EntitySelector(this.store, {
      name: 'AnswerSelector',
      entities: () => this.answers,
      filteredKeys: () => this.answerFilteredKeys
    });
  }

  mode: MomentSelectorMode = 'Topics';
  isVideoModeEnabled = false;

  highlightedMomentId: string | null = null;

  get highlightedMoment() {
    return this.job?.maybeGetMoment(this.highlightedMomentId);
  }

  get isTopicsMode() {
    return this.mode === 'Topics';
  }
  get isSpeakersMode() {
    return this.mode === 'Speakers';
  }
  get isCompaniesMode() {
    return this.mode === 'Companies';
  }

  get isInterviewMode() {
    return this.mode === 'Interview';
  }

  get outputMode() {
    if (this.isEmpty || !this.mode || this.isVideoModeEnabled)
      return 'Video';
    return this.mode;
  }

  get isTopicsOutputMode() {
    return this.outputMode === 'Topics';
  }
  get isSpeakersOutputMode() {
    return this.outputMode === 'Speakers';
  }
  get isCompaniesOutputMode() {
    return this.outputMode === 'Companies';
  }
  get isVideoOutputMode() {
    return this.outputMode === 'Video';
  }

  get qualifiedMode(): MomentSelectorQualifiedMode {
    return ((this.mode || '') + (this.isVideoOutputMode ? 'Video' : '')) as MomentSelectorQualifiedMode;
  }

  get isImplicitVideoOutputMode() {
    return this.isEmpty || !this.mode;
  }

  searchQuery: string | null = null;

  setSearchQuery(query: string) {
    this.searchQuery = query || null;
  }

  clearSearchQuery() {
    this.searchQuery = null;
  }

  // #region Resolved props
  get jobId() {
    return this.resolvedProps.jobId;
  }
  // #endregion

  get job(): Nullable<JobModel> {
    return this.store.maybeGetJob(this.jobId);
  }

  get isEmpty() {
    switch (this.mode) {
      case 'Topics':
        return this.topicSelector.isEmpty;

      case 'Speakers':
        return this.speakerSelector.isEmpty;
      
      case 'Interview':
        return this.questionSelector.isEmpty;
    }

    return true;
  }

  get queryable() {
    return fromMoments(this.moments);
  }

  get moments(): MomentModel[] {
    return this.job?.visibleMoments
      .filter(mom => !!mom.name || !!mom.description) || [];
  }

  get speakers(): Speaker[] {
    return this.job?.speakers || [];
  }

  get hasSpeakers(): boolean {
    return this.speakers.length > 0;
  }

  get generics(): MomentModel[] {
    return this.moments.filter(mom => mom.isGeneric);
  }
  get transcripts(): MomentModel[] {
    return this.moments.filter(mom => mom.isTranscript);
  }

  setHighlightedMoment(moment: MomentModel | string | null) {
    let id: string | null = null;
    if (isDefinedObject(moment))
      id = moment.id;
    else if (typeof moment === 'string')
      id = moment;

    this.highlightedMomentId = id;
  }

  clearHighlightedMoment() {
    this.highlightedMomentId = null;
  }

  // #region Topic selector
  // -------
  readonly topicSelector: EntitySelector<Topic>;

  topicSearchQuery: string | null = null;

  get topics(): Topic[] {
    return this.moments.filter(mom => mom.isTopic) as Topic[];
  }

  get hasTopics(): boolean {
    return this.topics.length > 0;
  }

  get selectedTopics(): Topic[] {
    return this.topicSelector.selectedEntities;
  }
  get filteredTopics(): Topic[] {
    return this.topicSelector.visibleEntities;
  }
  get filteredSelectedTopics(): Topic[] {
    return intersectionBy(
      this.selectedTopics,
      this.filteredTopics,
      top => top.id);
  }

  get topicFilteredKeys(): Set<string> | null {

    let data = this.topics;

    const topQuery = this.topicSearchQuery;
    if (topQuery)
      data = data.filter(matches(topQuery));

    const query = this.searchQuery;
    if (query)
      data = data.filter(matches(query));

    return new Set(data.map(sub => sub.id));
  }

  isTopicSelected(top: Topic | string): boolean {
    return this.topicSelector.isSelected(top);
  }
  isTopicFiltered(top: Topic | string): boolean {
    return this.topicSelector.isFiltered(top);
  }

  toggleTopic(top: Topic | string) {
    this.topicSelector.toggle(top);
  }
  selectTopic(top: Topic | string) {
    this.topicSelector.select(top);
  }
  unselectTopic(top: Topic | string) {
    this.topicSelector.unselect(top);
  }
  selectAllTopics() {
    this.topicSelector.selectAll();
  }
  clearTopics() {
    this.topicSelector.clear();
  }

  setTopicSearchQuery(query: string) {
    this.topicSearchQuery = query || null;
  }
  clearTopicSearchQuery() {
    this.topicSearchQuery = null;
  }
  // #endregion

  // #region SubTopic selector
  // -------
  readonly subTopicSelector: EntitySelector<SubTopic>;

  subTopicSearchQuery: string | null = null;

  get subTopics(): SubTopic[] {
    return this.moments.filter(mom => mom.isSubtopic) as SubTopic[];
  }

  get hasSubTopics(): boolean {
    return this.subTopics.length > 0;
  }

  get selectedSubTopics(): SubTopic[] {
    return this.subTopicSelector.selectedEntities;
  }
  get filteredSubTopics(): SubTopic[] {
    return this.subTopicSelector.visibleEntities;
  }
  get filteredSelectedSubTopics(): SubTopic[] {
    throw new Error('NotImplemented');
    // return this.subTopicSelector.visibleEntities;
  }

  get subTopicFilteredKeys(): Set<string> | null {

    const data = this.subTopics
      // only show subtopics connected to visible selected topics
      .filter(sub => this.filteredSelectedTopics.some(top => areConnected(sub, top)));

    // suppress search for now

    // const subQuery = this.subTopicSearchQuery;
    // if (subQuery)
    //   data = data.filter(matches(subQuery));

    // const query = this.searchQuery;
    // if (query)
    //   data = data.filter(matches(query));

    return new Set(data.map(sub => sub.id));
  }

  isSubTopicSelected(top: SubTopic | string): boolean {
    return this.subTopicSelector.isSelected(top);
  }
  isSubTopicFiltered(top: SubTopic | string): boolean {
    return this.subTopicSelector.isFiltered(top);
  }

  toggleSubTopic(top: SubTopic | string) {
    this.subTopicSelector.toggle(top);
  }
  selectSubTopic(top: SubTopic | string) {
    this.subTopicSelector.select(top);
  }
  unselectSubTopic(top: SubTopic | string) {
    this.subTopicSelector.unselect(top);
  }
  selectAllSubTopics() {
    this.subTopicSelector.selectAll();
  }
  clearSubTopics() {
    this.subTopicSelector.clear();
  }

  setSubTopicSearchQuery(query: string) {
    this.subTopicSearchQuery = query || null;
  }
  clearSubTopicSearchQuery() {
    this.subTopicSearchQuery = null;
  }

  selectSubTopicDefaults() {
    this.selectAllSubTopics();
  }

  getSubTopicsForTopic(topic: Topic | string): SubTopic[] {
    return this.topicSelector.get(topic)?.childSubTopics || [];
  }
  getSelectedSubTopicsForTopic(topic: Topic | string): SubTopic[] {
    return this.getSubTopicsForTopic(topic)
      .filter(sub => this.isSubTopicSelected(sub));
  }

  areAllSubTopicsSelectedForTopic(topic: Topic | string): boolean {
    return this.getSubTopicsForTopic(topic)
      .every(sub => this.isSubTopicSelected(sub));
  }
  areNoSubTopicsSelectedForTopic(topic: Topic | string): boolean {
    return !this.getSubTopicsForTopic(topic)
      .some(sub => this.isSubTopicSelected(sub));
  }
  /**
   * Returns true if some, **but not all**, subtopics are selected for the provided subtopic.
   */
  areSomeSubTopicsSelectedForTopic(topic: Topic | string): boolean {
    return (
      !this.areAllSubTopicsSelectedForTopic(topic) &&
      this.getSubTopicsForTopic(topic)
        .some(sub => this.isSubTopicSelected(sub)));
  }
  // #endregion

    // #region Question selector
  // -------
  readonly questionSelector: EntitySelector<Question>;

  questionSearchQuery: string | null = null;

  get questions(): Question[] {
    return this.moments.filter(mom => mom.isQuestion) as Question[];
  }

  get hasQuestions(): boolean {
    return this.questions.length > 0;
  }

  get selectedQuestions(): Question[] {
    return this.questionSelector.selectedEntities;
  }
  get filteredQuestions(): Question[] {
    return this.questionSelector.visibleEntities;
  }
  get filteredSelectedQuestions(): Question[] {
    return intersectionBy(
      this.selectedQuestions,
      this.filteredQuestions,
      top => top.id);
  }

  get questionFilteredKeys(): Set<string> | null {

    let data = this.questions;

    const topQuery = this.questionSearchQuery;
    if (topQuery)
      data = data.filter(matches(topQuery));

    const query = this.searchQuery;
    if (query)
      data = data.filter(matches(query));

    return new Set(data.map(sub => sub.id));
  }

  isQuestionSelected(question: Question | string): boolean {
    return this.questionSelector.isSelected(question);
  }
  isQuestionFiltered(question: Question | string): boolean {
    return this.questionSelector.isFiltered(question);
  }

  toggleQuestion(question: Question | string) {
    this.questionSelector.toggle(question);
  }
  selectQuestion(question: Question | string) {
    this.questionSelector.select(question);
  }
  unselectQuestion(question: Question | string) {
    this.questionSelector.unselect(question);
  }
  selectAllQuestions() {
    this.questionSelector.selectAll();
  }
  clearQuestions() {
    this.questionSelector.clear();
  }

  setQuestionSearchQuery(query: string) {
    this.questionSearchQuery = query || null;
  }
  clearQuestionSearchQuery() {
    this.questionSearchQuery = null;
  }
  // #endregion

  // #region Answers selector
  // -------
  readonly answerSelector: EntitySelector<Answer>;

  answerSearchQuery: string | null = null;

  get answers(): Answer[] {
    return this.moments.filter(mom => mom.isAnswer) as Answer[];
  }

  get hasAnswers(): boolean {
    return this.answers.length > 0;
  }

  get selectedAnswers(): Answer[] {
    return this.answerSelector.selectedEntities;
  }
  get filteredAnswers(): Answer[] {
    return this.answerSelector.visibleEntities;
  }
  get filteredSelectedAnswers(): Answer[] {
    throw new Error('NotImplemented');
    // return this.subTopicSelector.visibleEntities;
  }

  get answerFilteredKeys(): Set<string> | null {

    const data = this.answers
      // only show subtopics connected to visible selected topics
      .filter(sub => this.filteredSelectedAnswers.some(top => areConnected(sub, top)));

    // suppress search for now

    // const subQuery = this.subTopicSearchQuery;
    // if (subQuery)
    //   data = data.filter(matches(subQuery));

    // const query = this.searchQuery;
    // if (query)
    //   data = data.filter(matches(query));

    return new Set(data.map(sub => sub.id));
  }

  isAnswerSelected(ans: Answer | string): boolean {
    return this.answerSelector.isSelected(ans);
  }
  isAnswerFiltered(ans: Answer | string): boolean {
    return this.answerSelector.isFiltered(ans);
  }

  toggleAnswer(ans: Answer | string) {
    this.answerSelector.toggle(ans);
  }
  selectAnswer(ans: Answer | string) {
    this.answerSelector.select(ans);
  }
  unselectAnswer(ans: Answer | string) {
    this.answerSelector.unselect(ans);
  }
  selectAllAnswers() {
    this.answerSelector.selectAll();
  }
  clearAnswers() {
    this.answerSelector.clear();
  }

  setAnswerSearchQuery(query: string) {
    this.answerSearchQuery = query || null;
  }
  clearAnswerSearchQuery() {
    this.answerSearchQuery = null;
  }

  selectAnswerDefaults() {
    this.selectAllAnswers();
  }

  getAnswersForQuestion(question: Question | string): Answer[] {
    return this.questionSelector.get(question)?.childAnswers || [];
  }
  getSelectedAnswersForQuestion(question: Question | string): Answer[] {
    return this.getAnswersForQuestion(question)
      .filter(answer => this.isAnswerSelected(answer));
  }

  areAllAnswersSelectedForQuestion(question: Question | string): boolean {
    return this.getAnswersForQuestion(question)
      .every(answer => this.isAnswerSelected(answer));
  }
  areNoAnswersSelectedForQuestion(question: Question | string): boolean {
    return !this.getAnswersForQuestion(question)
      .some(answer => this.isAnswerSelected(answer));
  }
  /**
   * Returns true if some, **but not all**, subtopics are selected for the provided subtopic.
   */
  areSomeAnswersSelectedForQuestion(question: Question | string): boolean {
    return (
      !this.areAllAnswersSelectedForQuestion(question) &&
      this.getAnswersForQuestion(question)
        .some(answer => this.isAnswerSelected(answer)));
  }
  // #endregion

  // #region Speaker selector
  // -------
  readonly speakerSelector: EntitySelector<Speaker>;

  get selectedSpeakerIds(): Set<string> {
    return this.speakerSelector.selectedKeys;
  }

  get selectedSpeakers(): Speaker[] {
    return this.speakerSelector.selectedEntities;
  }

  get filteredSpeakers(): Speaker[] {
    return this.speakerSelector.visibleEntities;
  }
  get filteredSelectedSpeakers(): Speaker[] {
    return intersectionBy(
      this.selectedSpeakers,
      this.filteredSpeakers,
      top => top.id);
  }

  get speakerFilteredKeys(): Set<string> | null {

    let data = this.speakers;

    const query = this.searchQuery;
    if (query)
      data = data.filter(speakerMatches(query));

    return new Set(data.map(sub => sub.id));
  }

  isSpeakerSelected(speaker: Maybe<SpeakerModel | string>) {
    if (!speaker)
      return false;
    return this.speakerSelector.isSelected(speaker);
  }
  isSpeakerFiltered(speaker: Maybe<SpeakerModel | string>) {
    if (!speaker)
      return false;
    return this.speakerSelector.isFiltered(speaker);
  }

  toggleSpeaker(speaker: SpeakerModel | string) {
    return this.speakerSelector.toggle(speaker);
  }
  selectSpeaker(speaker: SpeakerModel | string) {
    return this.speakerSelector.select(speaker);
  }
  unselectSpeaker(speaker: SpeakerModel | string) {
    return this.speakerSelector.unselect(speaker);
  }
  selectAllSpeakers() {
    return this.speakerSelector.selectAll();
  }
  clearSpeakers() {
    return this.speakerSelector.clear();
  }
  // #endregion

  selectAll() {
    switch (this.mode) {
      case 'Topics':
        this.selectAllTopics();
        break;

      case 'Speakers':
        this.selectAllSpeakers();
        break;

      case 'Interview': {
        this.selectAllQuestions();
        break;
      }
    }
  }

  unselectAll() {
    this.clearSpeakers();
    this.selectSubTopicDefaults();
    this.clearTopics();
    this.clearAnswers();
    this.clearQuestions();
  }

  get selectedTotalDuration() {
    return duration(getTimeRegionsMergedDuration(this.selectedTopics));
  }

  get selectedTotalSavedDuration() {
    const { job } = this;
    if (!job)
      return duration(0);
    return duration(
      (job.videoDuration - this.selectedTotalDuration.as('seconds')) || 0);
  }

  clear() {
    this.unselectAll();

    this.clearSubTopicSearchQuery();
    this.clearTopicSearchQuery();
    this.clearAnswerSearchQuery();
    this.clearQuestionSearchQuery();
  }

  setMode(mode: MomentSelectorMode | null) {
    this.mode = (mode && ALLOWED_MODES.has(mode)) ? mode : DEFAULT_MODE;
  }

  setModeToTopics() {
    this.setMode('Topics');
  }
  setModeToSpeakers() {
    this.setMode('Speakers');
  }
  setModeToInterview() {
    this.setMode('Interview');
  }

  enterVideoMode() {
    this.isVideoModeEnabled = true;
  }
  exitVideoMode() {
    this.isVideoModeEnabled = false;
  }

  reset() {
    this.clear();
    this.setHighlightedMoment(null);
    this.setModeToTopics();
  }

  export(): MomentSelectorData {
    this.purge();

    return {
      mode: this.mode,
      isVideoModeEnabled: this.isVideoModeEnabled,
      
      topicSelector: this.topicSelector.export(),
      subTopicSelector: this.subTopicSelector.export(),
      speakerSelector: this.speakerSelector.export(),
      answerSelector: this.answerSelector.export(),
      questionSelector: this.questionSelector.export(),
    }
  }

  import(data: MomentSelectorData): this {
    this.clear();

    this.setMode(data.mode);
    this.isVideoModeEnabled = data.isVideoModeEnabled;

    if (data.topicSelector)
      this.topicSelector.import(data.topicSelector);
    if (data.subTopicSelector)
      this.subTopicSelector.import(data.subTopicSelector);
    if (data.speakerSelector)
      this.speakerSelector.import(data.speakerSelector);
    if (data.questionSelector)
      this.questionSelector.import(data.questionSelector);
    if (data.answerSelector)
      this.answerSelector.import(data.answerSelector);

    this.purge();
    return this;
  }

  serialize(): string {
    return JSON.stringify(this.export());
  }

  deserialize(data?: string | null): this {
    if (!data) {
      return this;
    }

    this.import(JSON.parse(data));
    return this;
  }

  save() {
    const { storage } = this.store;
    if (!this.jobId)
      return false;

    const stateStr = storage.getLocal(MOMENT_SELECTOR_STATE_KEY);
    let state: ObjectLiteral<MomentSelectorData> = {};
    if (stateStr) {
      try {
        state = JSON.parse(stateStr);
      } catch (_e) {
        state = {};
      }
    }

    if (!isDefinedObject(state))
      state = {};

    state[this.jobId] = this.export();

    storage.setLocal(MOMENT_SELECTOR_STATE_KEY, JSON.stringify(state));
  }

  load() {
    const { storage } = this.store;
    if (!this.jobId)
      return false;

    const stateStr = storage.getLocal(MOMENT_SELECTOR_STATE_KEY);
    let state: ObjectLiteral<MomentSelectorData> = {};
    if (stateStr) {
      try {
        state = JSON.parse(stateStr);
      } catch (_e) {
        state = {};
      }
    }

    if (!isDefinedObject(state))
      state = {};

    const data = state[this.jobId];
    if (isDefinedObject(data))
      this.import(data);
  }

  /** Removes all selector keys that don't match any moments. */
  purge() {
    this.topicSelector.purge();
    this.subTopicSelector.purge();
    this.speakerSelector.purge();
    this.answerSelector.purge();
    this.questionSelector.purge();
  }

  /** Invoked when a moment has been newly added or updated. */
  handleMomentMutated(momId: string) {
    const mom = this.job?.maybeGetMoment(momId);
    if (!mom)
      return;

    switch (this.outputMode) {
      case 'Topics':
        if (mom.isTopic) {
          this.topicSelector.select(mom as Topic);
          break;
        }

        if (mom.isSubtopic) {
          this.subTopicSelector.select(mom as SubTopic);
          break;
        }
        break;

      case 'Speakers':
        if (mom.hasSpeaker && mom.actualSpeakerId) {
          this.speakerSelector.select(mom.actualSpeakerId);
          break;
        }
        break;
    }

    this.purge();
  }

  handleMomentDeleted(_momId: string) {
    this.purge();
  }
}